// #region Imports

import * as React from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { IStateTree } from '../redux/store';
import { INoteData } from '../dto/INoteData';
import { actionCreators, setHighlightedCourseIndex, setRelatedFilter, setFilterPanelOpen, setEditingNoteId, IRelatedFilter } from '../redux/modules/document';
import { ICourseData } from '../dto/ICourseData';
import { NoteContainer } from './note';
import NoteTooltip from './noteTooltip';
import RelatedMaterialFilter from './relatedMaterialFilter';
import { IPosition } from '../dto/IPosition';
import { SelectorService } from '../services/SelectorService';
import { NoteService } from '../services/noteService';
import { noteMatchesFilter, courseMatchesFilter, countActiveFilters } from '../modules/helpers/relatedFilter';

// #endregion Imports

// #region Dispatchers

interface INoteSidebarComponentDispatch {
    onHideTooltip?: () => void;
    onMarkingCreated?: (note: INoteData) => void;
    onNoteCreated?: (note: INoteData) => void;
    onNoteOpenToggleVisibility?: (visibility: boolean) => void;
    rollDownExpanded?: (visibility: boolean) => void;
    setNotesPanelExpaned?: (visibility: boolean) => void;
    setCurrentNote?: (note: INoteData) => void;
    setHighlightedCourseIndex?: (index: number | null) => void;
    aNoteWasUpdatedFunc?: () => void;
    setRelatedFilter?: (filter: IRelatedFilter) => void;
    setFilterPanelOpen?: (open: boolean) => void;
}

// #endregion Dispatchers

// #region Props

interface INoteSidebarComponentProps extends INoteSidebarComponentDispatch {
    notes: INoteData[];
    showNotes: boolean;
    showTooltip: boolean;
    tooltipPosition: IPosition;
    updateNoteCollisionAndBundling: number;
    aNoteWasUpdated: number;
    notesPanelExpaned: boolean;
    currentNote: INoteData;
    editingNoteId: string | null;
    courses: ICourseData[];
    relatedFilter: IRelatedFilter;
    filterPanelOpen: boolean;
}

// #endregion Props


// #region State

export interface INoteComponentState {
    currentTargetNote: number;
    prevNoteBtnDisabled: boolean;
    nextNoteBtnDisabled: boolean;
    handleScrollActive: boolean;
    isBigScreen: boolean;
    categories: [];
    iconPositions: Record<string, number>;
    hoveredMarkingNoteId: string | null;
}

// #endregion State

const MEDIUM_DESKTOP = 1460;
const DESKTOP = 1200;

// #region Component

class NoteSidebarComponent extends React.Component<INoteSidebarComponentProps, INoteComponentState> {

    private stackSettleTimer: any = null;
    private stackRafPending = false;
    private scrollRafPending = false;
    private suppressAutoScroll = false;
    private headerResizeObserver: ResizeObserver | null = null;
    // Headerns underkant vid scrolltopp, som OFFSET relativt #documentcontents ovankant
    // (inte en absolut sid-koordinat – se measureHeaderClampAtScrollTop för varför)
    private headerClampOffsetFromContent: number | null = null;

    constructor(props) {
        super(props);
        const total = (props.courses?.length ?? 0) + (props.notes?.length ?? 0);
        this.state = {
            currentTargetNote: -1,
            prevNoteBtnDisabled: true,
            nextNoteBtnDisabled: total <= 0,
            handleScrollActive: true,
            isBigScreen: false,
            categories: [],
            iconPositions: {},
            hoveredMarkingNoteId: null,
        }
    }

    componentDidMount() {
        window.addEventListener('scroll', this.handleScrollThrottled);
        this.moveSelector();

        document.addEventListener("keydown", (e) => this.closeAllOpenEdits(e), false);
        $(".addNoteIcon")
            .on("mouseenter", () => { $(".tooltipAddNote").show(); })
            .on("mouseleave", () => { $(".tooltipAddNote").hide(); });

        this.handleScroll();
        window.addEventListener("resize", this.handleWindowResize.bind(this));
        this.handleWindowResize();
        this.calculateIconPositions();
        this.scheduleStackOverlappingNotes();
        const docContent = document.getElementById('documentcontent');
        docContent?.addEventListener('click', this.markingClickHandler);
        docContent?.addEventListener('keydown', this.markingKeydownHandler);
        docContent?.addEventListener('mouseover', this.markingMouseOverHandler);
        docContent?.addEventListener('mouseout', this.markingMouseOutHandler);
        this.makeMarkingsAccessible();
        this.props.aNoteWasUpdatedFunc && this.props.aNoteWasUpdatedFunc();

        const service = new NoteService();
        service.getNoteCategories((data) => {
            this.setState({ categories: data });
        }, (err) => {
            console.log('could not get categories', err);
        });

        this.setupAddNoteIconHeaderTracking();
        document.addEventListener('click', this.handleOutsideFilterClick, true);

        setTimeout(() => this.handleInitialHashNavigation(), 400);
    }

    private handleOutsideFilterClick = (e: MouseEvent) => {
        if (!this.props.filterPanelOpen) return;
        const target = e.target as HTMLElement;
        if (target.closest('.panel-filtrera-wrapper')) return;
        this.props.setFilterPanelOpen?.(false);
    }

    private setupAddNoteIconHeaderTracking() {
        const header = document.querySelector('.document-sticky__document-header-wrapper');
        if (!header || typeof ResizeObserver === 'undefined') return;

        this.positionAddNoteIconBelowHeader();

        this.headerResizeObserver = new ResizeObserver(() => {
            this.positionAddNoteIconBelowHeader();
            this.refreshHeaderClampAtScrollTop();
        });
        this.headerResizeObserver.observe(header);
    }

    private positionAddNoteIconBelowHeader = () => {
        const fixedNote = document.querySelector('.note--fixed') as HTMLElement | null;
        if (!fixedNote) return;
        fixedNote.style.top = `${Math.max(this.getHeaderBottom(), 0)}px`;
    }
    private measureHeaderClampAtScrollTop(): number | null {
        if (window.scrollY >= 1) return null;
        if (document.body.classList.contains('with-minimized-header')) return null;
        const contentEl = document.getElementById('documentcontent');
        if (!contentEl) return null;
        return this.getHeaderBottom() - contentEl.getBoundingClientRect().top;
    }

    private refreshHeaderClampAtScrollTop = () => {
        const value = this.measureHeaderClampAtScrollTop();
        if (value === null) return;
        if (this.headerClampOffsetFromContent !== null && Math.abs(value - this.headerClampOffsetFromContent) < 1) return;
        this.headerClampOffsetFromContent = value;
        this.scheduleStackOverlappingNotes();
    }

    moveSelector() {
        const notesSelector = document.querySelector('.notesSelectorWrapper');
        if (!notesSelector) return;

        const headerRight = document.querySelector('.document-sticky__document-header-right');
        if (headerRight && notesSelector.parentElement !== headerRight) {
            headerRight.appendChild(notesSelector);
        }
    }

    private handleWindowResize() {
        this.setState({ isBigScreen: window.innerWidth >= MEDIUM_DESKTOP });
        // Note tops & icon positions are layout-dependent – recompute when the viewport changes
        this.scheduleStackOverlappingNotes();
        this.calculateIconPositions();
        // Mät om när layouten satt sig (sidofältets breddtransition är 0.3s)
        setTimeout(() => this.calculateIconPositions(), 400);
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.handleScrollThrottled);
        const docContent = document.getElementById('documentcontent');
        docContent?.removeEventListener('click', this.markingClickHandler);
        docContent?.removeEventListener('keydown', this.markingKeydownHandler);
        docContent?.removeEventListener('mouseover', this.markingMouseOverHandler);
        docContent?.removeEventListener('mouseout', this.markingMouseOutHandler);
        if (this.stackSettleTimer) clearTimeout(this.stackSettleTimer);
        this.headerResizeObserver?.disconnect();
        document.removeEventListener('click', this.handleOutsideFilterClick, true);
    }

    private makeMarkingsAccessible = () => {
        this.props.notes?.forEach(note => {
            const el = document.getElementById('marking-' + note.id);
            if (!el) return;
            el.setAttribute('tabindex', '0');
            el.setAttribute('role', 'button');
            el.setAttribute('aria-label', note.text ? `Markering: ${note.text}` : 'Markering');
        });
    }
    private activateNote = (note: INoteData) => {
        if (window.innerWidth < DESKTOP) {
            this.props.setCurrentNote?.(note);
            this.props.setNotesPanelExpaned?.(true);
            return;
        }

        if (!this.props.notesPanelExpaned) {
            this.props.setNotesPanelExpaned?.(true);
        }

        requestAnimationFrame(() => {
            const sortedNotes = this.buildNotesBasedOnPosition();
            const noteIndex = sortedNotes.findIndex(n => n.id === note.id);
            if (noteIndex < 0) return;
            const courseCount = this.getVisibleCourseCount();
            this.setState({ currentTargetNote: courseCount + noteIndex, handleScrollActive: false });
        });
    }

    private activateMarkingElement = (marking: Element) => {
        const note = this.props.notes.find(n => marking.classList.contains('marking-' + n.id));
        if (!note) return;
        this.activateNote(note);
    }

    private handleInitialHashNavigation = () => {
        const hash = window.location.hash.replace('#', '');
        if (!hash) return;
        const note = this.props.notes?.find(n => n.markupId === hash);
        if (note) this.activateNote(note);
    }

    private markingClickHandler = (e: MouseEvent) => {
        const marking = (e.target as HTMLElement).closest?.('.marking');
        if (marking) this.activateMarkingElement(marking);
    }

    private markingKeydownHandler = (e: KeyboardEvent) => {
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        const marking = (e.target as HTMLElement).closest?.('.marking');
        if (!marking) return;
        e.preventDefault();
        this.activateMarkingElement(marking);
    }

    // Hover på en markering ska bara ge det kopplade kortet aktiv styling – inte scrolla eller ändra nav-index
    private markingMouseOverHandler = (e: MouseEvent) => {
        const marking = (e.target as HTMLElement).closest?.('.marking');
        if (!marking) return;
        const note = this.props.notes.find(n => marking.classList.contains('marking-' + n.id));
        if (note && this.state.hoveredMarkingNoteId !== note.id) {
            this.setState({ hoveredMarkingNoteId: note.id });
        }
    }

    private markingMouseOutHandler = (e: MouseEvent) => {
        const marking = (e.target as HTMLElement).closest?.('.marking');
        if (!marking || !this.state.hoveredMarkingNoteId) return;
        const related = e.relatedTarget as HTMLElement | null;
        const stillOnSameNote = related?.closest?.('.marking-' + this.state.hoveredMarkingNoteId);
        if (!stillOnSameNote) {
            this.setState({ hoveredMarkingNoteId: null });
        }
    }

    private closeAllOpenEdits(e) {
        if (e === null) {
            setTimeout(() => {
                this.props.onNoteOpenToggleVisibility && this.props.onNoteOpenToggleVisibility(false);
            }, 60);
            return;
        } else if (e.key === "Escape") {
            if ($(".dropdown").hasClass("open")) {
                $(".dropdown").find(".closeIcon").trigger("click");
            }
            //$(".note-editor").hide();
        }
        $(".js-add-note").removeClass("active");
    }

    // Antal kurser som klarar det aktiva filtret – används för courseCount-offseten i
    // all index-baserad navigering, så den matchar de kurskort som faktiskt renderas.
    private getVisibleCourseCount(): number {
        return (this.props.courses ?? []).filter(c => courseMatchesFilter(c, this.props.relatedFilter)).length;
    }

    private buildNotesBasedOnPosition(): INoteData[] {
        const arrOfNotes: Array<{ position: number | undefined, note: INoteData }> = [];

        this.props.notes
            ?.filter(note => noteMatchesFilter(note, this.props.relatedFilter, this.props.editingNoteId))
            .forEach((note) => {
            arrOfNotes.push({
                position: $("#" + note.id).offset()?.top,
                note,
            });
        });

        arrOfNotes.sort((a, b) => {
            if (a.position === undefined || b.position === undefined) return 0;
            return a.position - b.position;
        });

        return arrOfNotes.map(item => item.note);
    }

    private getHeaderBottom(): number {
        const header = document.querySelector('.document-sticky__document-header-right');
        return header ? header.getBoundingClientRect().bottom : 0;
    }

    private getCardElements(): (Element | null)[] {
        const courseCount = this.getVisibleCourseCount();
        const courseEls = document.querySelectorAll('.course-cards-sidebar .course-card');
        const cards: (Element | null)[] = [];
        for (let i = 0; i < courseCount; i++) {
            cards.push(courseEls[i] ?? null);
        }
        this.buildNotesBasedOnPosition().forEach(note =>
            cards.push(document.getElementById('note-editor-' + note.id) || document.getElementById(note.id))
        );
        return cards;
    }

    private handleScrollThrottled = () => {
        if (this.scrollRafPending) return;
        this.scrollRafPending = true;
        requestAnimationFrame(() => {
            this.scrollRafPending = false;
            this.handleScroll();
        });
    }

    private handleScroll = () => {
        if (!this.state.handleScrollActive) return;

        const cards = this.getCardElements();
        if (!cards.some(el => el)) return;

        const headerBottom = this.getHeaderBottom();

        let targetIndex = -1;
        for (let i = 0; i < cards.length; i++) {
            const el = cards[i];
            if (!el) continue;
            if (el.getBoundingClientRect().top <= headerBottom) {
                targetIndex = i;
            } else {
                break;
            }
        }

        this.setState({ currentTargetNote: targetIndex });
    }

    // componentDidUpdate can trigger this from several independent branches within
    // the same update (e.g. notes + panel state changing together) – coalesce those
    // into a single recompute per frame instead of redoing the full O(n) DOM pass once per branch.
    private iconPositionsRafPending = false;
    private calculateIconPositions() {
        if (this.iconPositionsRafPending) return;
        this.iconPositionsRafPending = true;
        requestAnimationFrame(() => {
            this.iconPositionsRafPending = false;
            this.calculateIconPositionsImpl();
        });
    }

    private calculateIconPositionsImpl() {
        const ICON_HEIGHT = 32; // matchar .note-icon i SCSS
        const GAP = 8;

        const container = document.querySelector('.notesContentWrapper');
        const containerTop = container ? container.getBoundingClientRect().top : 0;

        const items = this.props.notes
            .filter(note => noteMatchesFilter(note, this.props.relatedFilter, this.props.editingNoteId))
            .filter(note => note.elementId && note.elementId !== '#' && note.elementId !== '#documentcontent')
            .map(note => {
                const el = $(note.elementId).get(0);
                if (!el) return null;
                const top = el.getBoundingClientRect().top - containerTop;
                return { id: note.id, top: Math.max(top, 80) };
            })
            .filter((x): x is { id: string; top: number } => x !== null)
            .sort((a, b) => a.top - b.top);

        // Stapla så att ikonerna aldrig överlappar varandra
        const positions: Record<string, number> = {};
        let prevBottom = 0;
        items.forEach(({ id, top }) => {
            const placedTop = prevBottom > 0 ? Math.max(top, prevBottom + GAP) : top;
            positions[id] = placedTop;
            prevBottom = placedTop + ICON_HEIGHT;
        });

        this.setState({ iconPositions: positions });
    }

    private stackOverlappingNotes() {
        const floatingNotes = $(".note--floating").toArray();
        if (!floatingNotes.length) return;

        const GAP = 8;

        // Reset to natural CSS position, then measure in true vertical order
        floatingNotes.forEach(el => $(el).css("margin-top", ""));

        // Släpp en tidigare körnings container-utökning innan vi mäter om, annars växer den för varje körning
        const contentEl = document.getElementById('documentcontent');
        if (contentEl) contentEl.style.paddingBottom = "";

        const scrollY = window.scrollY;
        const measured = floatingNotes
            .map(el => {
                const $el = $(el);
                const baseMargin = parseFloat($el.css("margin-top")) || 0;

                // Mät det synliga anteckningskortet (.note-editor) – dess faktiska top/bottom.
                // Cirkeln (.noteFold) är bara ankare (och dess box kan vara högre än ett
                // kort kort), så vi spacar utifrån kortet, inte en fast höjd.
                const editor = el.querySelector('.note-editor') as HTMLElement | null;
                let cardTop: number, cardBottom: number;
                if (editor && editor.offsetHeight > 0) {
                    const r = editor.getBoundingClientRect();
                    cardTop = r.top + scrollY;
                    cardBottom = r.bottom + scrollY;
                } else {
                    const fold = el.querySelector('.noteFold') as HTMLElement | null;
                    if (fold) {
                        const r = fold.getBoundingClientRect();
                        cardTop = r.top + scrollY;
                        cardBottom = r.bottom + scrollY;
                    } else {
                        cardTop = $el.offset()?.top ?? 0;
                        cardBottom = cardTop + 40;
                    }
                }

                return { el, baseMargin, cardTop, cardBottom, cardHeight: cardBottom - cardTop };
            })
            .sort((a, b) => a.cardTop - b.cardTop);

        // Notes that overlap the course cards must stack below them
        const courseCardsEl = document.querySelector('.course-cards-sidebar');
        const courseCardsBottom = courseCardsEl
            ? courseCardsEl.getBoundingClientRect().bottom + scrollY
            : 0;

        // Korten får inte sträcka sig nedanför dokumentinnehållet (ut över footern) –
        // om de gör det utökas containern istället för att korten klampas ihop
        const contentBottom = contentEl
            ? contentEl.getBoundingClientRect().bottom + scrollY
            : Infinity;

        const clampOffset = this.measureHeaderClampAtScrollTop();
        if (clampOffset !== null) {
            this.headerClampOffsetFromContent = clampOffset;
        }
        const contentTop = contentEl ? contentEl.getBoundingClientRect().top + scrollY : 0;
        const headerClamp = this.headerClampOffsetFromContent !== null
            ? contentTop + this.headerClampOffsetFromContent
            : 0;
        let prevCardBottom = Math.max(courseCardsBottom, headerClamp);

        measured.forEach(({ el, baseMargin, cardTop, cardBottom, cardHeight }) => {
            // Skjut ned kortet om det ligger närmare än GAP under föregående kort
            const desiredCardTop = (prevCardBottom > 0 && cardTop < prevCardBottom + GAP)
                ? prevCardBottom + GAP
                : cardTop;

            const shift = desiredCardTop - cardTop;
            $(el).css("margin-top", `${baseMargin + shift}px`);
            prevCardBottom = desiredCardTop + cardHeight;
        });

        // Om sista kortet sträcker sig nedanför dokumentinnehållet, väx containern med
        // exakt det överskjutande måttet så inget kort kan hamna ovanpå footern
        if (contentEl) {
            const overflow = prevCardBottom - contentBottom;
            contentEl.style.paddingBottom = overflow > 0 ? `${overflow}px` : "";
        }
    }

    private scheduleStackOverlappingNotes() {
        if (!this.stackRafPending) {
            this.stackRafPending = true;
            requestAnimationFrame(() => {
                this.stackRafPending = false;
                this.stackOverlappingNotes();
                this.handleScroll();
            });
        }

        if (this.stackSettleTimer) clearTimeout(this.stackSettleTimer);
        this.stackSettleTimer = setTimeout(() => { this.stackOverlappingNotes(); this.handleScroll(); }, 400);
    }

    componentDidUpdate(prevProps: Readonly<INoteSidebarComponentProps>, prevState: Readonly<INoteComponentState>) {
        if (this.state.isBigScreen !== prevState.isBigScreen) {
            if (this.state.isBigScreen) {
                this.props.onNoteOpenToggleVisibility && this.props.onNoteOpenToggleVisibility(true);
                this.props.rollDownExpanded && this.props.rollDownExpanded(false);
            } else {
                this.props.onNoteOpenToggleVisibility && this.props.onNoteOpenToggleVisibility(false);
            }
        }

        // Körs även om currentTargetNote råkar vara oförändrat men handleScrollActive
        // just stängdes av (t.ex. activateNote via hash-navigering från notes__sublist
        // i globala menyn, där webbläsarens egen #-scroll redan landat på rätt kort).
        // Annars återaktiveras aldrig handleScroll och det aktiva kortet "fastnar".
        const justSuppressedScroll = this.state.handleScrollActive !== prevState.handleScrollActive && !this.state.handleScrollActive;
        if (this.state.currentTargetNote !== prevState.currentTargetNote || justSuppressedScroll) {
            const courseCount = this.getVisibleCourseCount();
            const total = courseCount + this.buildNotesBasedOnPosition().length;
            const idx = this.state.currentTargetNote;

            this.setState({
                prevNoteBtnDisabled: idx <= 0,
                nextNoteBtnDisabled: idx >= total - 1,
            });

            // Endast programmatisk navigering (pilknappar) ska scrolla. När indexet
            // ändras p.g.a. användarens egen scroll (handleScrollActive=true) ska vi
            // bara uppdatera markering/siffra utan att kapa scrollen.
            // suppressAutoScroll: note-icon-klicket sköter sin egen villkorliga scroll.
            const programmatic = !this.state.handleScrollActive && !this.suppressAutoScroll;
            // currentNote styr markeringens starkare färg i texten – vid ren scroll ska den nollställas, inte peka på scrollad-till kort
            const activatedByNavigation = !this.state.handleScrollActive;
            this.suppressAutoScroll = false;

            if (idx < 0) {
                // Inget kort har nått headerns nederkant än – inget ska vara markerat.
                this.props.setHighlightedCourseIndex?.(null);
                this.props.setCurrentNote?.(null as any);
            } else if (idx < courseCount) {
                // Kurskort
                this.props.setHighlightedCourseIndex?.(idx);
                this.props.setCurrentNote?.(null as any);
                // handleScrollActive återaktiveras när scrollningen faktiskt är klar, inte efter en gissad fördröjning
                if (programmatic) requestAnimationFrame(() => this.scrollToCourseCard(idx, () => this.setState({ handleScrollActive: true })));
            } else {
                // Anteckning
                this.props.setHighlightedCourseIndex?.(null);
                const sortedNotes = this.buildNotesBasedOnPosition();
                const note = sortedNotes[idx - courseCount];
                this.props.setCurrentNote?.(activatedByNavigation ? note : (null as any));
                if (programmatic) requestAnimationFrame(() => this.scrollToTargetNote(() => this.setState({ handleScrollActive: true })));
            }
        }

        if (this.props.notes !== prevProps.notes) {
            this.scheduleStackOverlappingNotes();
            this.calculateIconPositions();
            this.makeMarkingsAccessible();
            if (this.buildNotesBasedOnPosition().length === 0) {
                this.setState({ prevNoteBtnDisabled: true, nextNoteBtnDisabled: true });
            }
            this.props.onNoteOpenToggleVisibility && this.props.onNoteOpenToggleVisibility(true);
        }

        if (this.props.notesPanelExpaned !== prevProps.notesPanelExpaned) {
            this.calculateIconPositions();
            // Kurskorten monteras/avmonteras med panelen – stapla om noterna
            this.scheduleStackOverlappingNotes();
        }

        if (this.props.courses !== prevProps.courses) {
            this.scheduleStackOverlappingNotes();
        }

        if (this.props.updateNoteCollisionAndBundling !== prevProps.updateNoteCollisionAndBundling) {
            this.scheduleStackOverlappingNotes();
        }

        // Notes recompute their top on this signal (debounced in note.tsx) – re-stack after.
        if (this.props.aNoteWasUpdated !== prevProps.aNoteWasUpdated) {
            this.scheduleStackOverlappingNotes();
            this.calculateIconPositions();
            // Markeringar kan ha renderats efter mount – säkerställ att de är fokuserbara
            this.makeMarkingsAccessible();
        }

        // Filtret ändrat: enbart kort som klarar filtret ska renderas – stapla om,
        // räkna om ikonpositioner, och klampa nav-index om det pekar utanför den
        // nya (kortare) synliga listan.
        if (this.props.relatedFilter !== prevProps.relatedFilter) {
            this.calculateIconPositions();
            this.scheduleStackOverlappingNotes();

            const visibleTotal = this.getVisibleCourseCount() + this.buildNotesBasedOnPosition().length;
            if (visibleTotal === 0) {
                this.setState({ currentTargetNote: -1, prevNoteBtnDisabled: true, nextNoteBtnDisabled: true });
            } else if (this.state.currentTargetNote >= visibleTotal) {
                this.setState({ currentTargetNote: visibleTotal - 1 });
            }
        }
    }

    private onTooltipClose = () => {
        this.props.onHideTooltip && this.props.onHideTooltip();
    }

    private onAddMarker = () => {
        this.createFromSelection(false);
        this.props.onNoteOpenToggleVisibility && this.props.onNoteOpenToggleVisibility(true);
        this.forceUpdate();
    }

    private onAddNote = () => {
        this.createFromSelection(true);
        this.props.onNoteOpenToggleVisibility && this.props.onNoteOpenToggleVisibility(true);
        this.forceUpdate();
        this.scheduleStackOverlappingNotes();
    }

    private removeHtmlCodes(inputString: string): string {
        const tempElement = document.createElement("div");
        tempElement.innerHTML = inputString;
        return tempElement.textContent || tempElement.innerText;
    }

    private onAddNoteClick = () => {
        if (!this.props.onNoteCreated) return;

        const element = $('.js-add-note');
        element.addClass("active");

        const position = element.offset()?.top;
        const elementId = (window as any)._getElementFromPosition(position);
        let headerOfElement = "";

        if (elementId) {
            if ($("#" + elementId).is('h2') || $("#" + elementId).is('h3')) {
                headerOfElement = this.removeHtmlCodes($("#" + elementId).html());
            } else {
                if ($("#" + elementId).siblings("h2").html()) {
                    headerOfElement = this.removeHtmlCodes($("#" + elementId).siblings("h2").html());
                }
                if (headerOfElement === "" && $("#" + elementId).siblings("h3").html()) {
                    headerOfElement = this.removeHtmlCodes($("#" + elementId).siblings("h3").html());
                }
            }
        }

        const note: INoteData = {
            id: '',
            color: 'color1',
            markupId: elementId,
            pageId: (window as any).contentId,
            selection: '',
            text: '',
            title: headerOfElement,
            pageHeading: headerOfElement,
            elementId: `#${elementId}`,
            selectionObject: null,
            openEditorAtStart: true,
            fumbled: false,
        };

        const service = new NoteService();
        service.createNote(note,
            (noteData: INoteData) => {
                noteData.openEditorAtStart = true;
                
                noteData.elementId = note.elementId;
                this.props.onNoteCreated && this.props.onNoteCreated(noteData);
                this.stackOverlappingNotes();
                $("#note-editor-" + noteData.id).focus();
                this.scrollToNewEditor(noteData.id);

            },
            () => {
                alert('Ett fel har uppstått. Anteckningen kunde inte sparas');
            });

        this.forceUpdate();

        if (window.innerWidth < DESKTOP) {
            this.closeAllOpenEdits(null);
        }
    }

    private createFromSelection = (openEditor: boolean) => {
        const note = SelectorService.instance.saveSelection();
        const service = new NoteService();
        const selectionObject = note.selectionObject;

        service.createNote(note,
            (noteData: INoteData) => {
                const markerId = `marking-${noteData.id}`;
                $('x-marker.marking--new')
                    .removeClass('marking--new')
                    .addClass(markerId)
                    .first()
                    .attr('id', markerId);

                noteData.elementId = `#${markerId}`;
                noteData.selectionObject = selectionObject;
                this.stackOverlappingNotes();

                if (openEditor) {
                    noteData.openEditorAtStart = true;
                    this.props.onNoteCreated && this.props.onNoteCreated(noteData);
                    this.scrollToNewEditor(noteData.id);
                } else {
                    this.props.onMarkingCreated && this.props.onMarkingCreated(noteData);
                }
            },
            () => {
                alert('Ett fel har uppstått. Markeringen kunde inte sparas');
                SelectorService.instance.remove(note.elementId);
            });
    }

    private goToNextNote = () => {
        const total = this.getVisibleCourseCount() + this.buildNotesBasedOnPosition().length;
        if (this.state.currentTargetNote < total - 1) {
            this.setState({ currentTargetNote: this.state.currentTargetNote + 1, handleScrollActive: false });
        }
    }

    private goToPrevNote = () => {
        if (this.state.currentTargetNote > 0) {
            this.setState({ currentTargetNote: this.state.currentTargetNote - 1, handleScrollActive: false });
        }
    }

    // Scrollar så att kortets ovankant hamnar precis under header-baren, dvs samma
    // läge som handleScroll betraktar som "översta synliga kort" → index blir konsekvent.
    // onComplete triggas när scrollningen faktiskt är klar (via scrollend, med timeout-fallback)
    // istället för efter en gissad fördröjning – annars hinner handleScroll skriva över
    // valet innan ett långt scroll (t.ex. mellan glest placerade anteckningar) är färdigt.
    private scrollCardBelowHeader(el: Element, onComplete?: () => void) {
        const headerBottom = this.getHeaderBottom();
        const delta = el.getBoundingClientRect().top - headerBottom - 2;
        window.scrollBy({ top: delta, behavior: 'smooth' });

        if (!onComplete) return;
        if (Math.abs(delta) < 1) { onComplete(); return; }

        let settled = false;
        const finish = () => {
            if (settled) return;
            settled = true;
            window.removeEventListener('scrollend', finish);
            clearTimeout(fallback);
            onComplete();
        };
        const fallback = setTimeout(finish, 2000);
        window.addEventListener('scrollend', finish);
    }

    private scrollToCourseCard = (courseIndex: number, onComplete?: () => void) => {
        const cards = document.querySelectorAll('.course-cards-sidebar .course-card');
        if (cards[courseIndex]) {
            this.scrollCardBelowHeader(cards[courseIndex], onComplete);
        } else {
            onComplete?.();
        }
    }

    private scrollToTargetNote = (onComplete?: () => void) => {
        const sortedNotes = this.buildNotesBasedOnPosition();
        const courseCount = this.getVisibleCourseCount();
        const noteIndex = this.state.currentTargetNote - courseCount;
        const id = sortedNotes[noteIndex]?.id;
        if (!id) { onComplete?.(); return; }

        // Samma element som handleScroll mäter – hela anteckningskortet
        const el = document.getElementById('note-editor-' + id) || document.getElementById(id);
        if (el) this.scrollCardBelowHeader(el, onComplete);
        else onComplete?.();
    }

    // Efter att en ny anteckning skapats och dess editor öppnats: om editorn staplats
    // långt ner och hamnat under vikningen, scrolla ner så att den syns. Fördröjt så
    // att panel-expansion, editor-öppning och omstapling hunnit sätta sig.
    private scrollToNewEditor = (noteId: string) => {
        setTimeout(() => {
            const el = document.getElementById('note-editor-' + noteId) as HTMLElement | null;
            if (!el || el.offsetHeight === 0) return; // inte renderad/synlig (panelen ihopfälld)
            if (!this.isCardVisible(el)) {
                this.scrollCardBelowHeader(el);
            }
        }, 450);
    }

    // Är kortet redan tillräckligt synligt så att vi kan undvika att scrolla?
    // Kravet: ovankanten ligger under header-baren (inte dold) och en rimlig del av
    // kortet syns (för höga kort räcker att ovankanten + en bit visas). Då slipper vi
    // onödigt hopp – vi scrollar bara när kortets början är dold bakom headern eller
    // ligger (nästan) under vikningen.
    private isCardVisible(el: Element): boolean {
        const rect = el.getBoundingClientRect();
        const headerBottom = this.getHeaderBottom();
        const viewportBottom = window.innerHeight;
        if (rect.top < headerBottom) return false;      // dold bakom headern
        if (rect.top >= viewportBottom) return false;   // helt under vikningen
        const visible = Math.min(rect.bottom, viewportBottom) - rect.top;
        return visible >= Math.min(rect.height, 80);
    }

    private handleNoteIconClick = (note: INoteData) => {
        // Mobil (<1200px): öppna modalen; noteShowOrHide scrollar till kortet och
        // markerar det (via currentNote). Ingen dokument-scroll här.
        if (window.innerWidth < DESKTOP) {
            this.props.setCurrentNote?.(note);
            this.props.setNotesPanelExpaned?.(true);
            return;
        }

        if (!this.props.notesPanelExpaned) {
            this.props.setNotesPanelExpaned && this.props.setNotesPanelExpaned(true);
        }

        // Vänta tills panelen expanderat och kortet renderats
        requestAnimationFrame(() => {
            // Stapla korten FÖRST så de har sin slutliga position innan isCardVisible mäter.
            // (Panel-expansionen schemalägger också en stapling i en egen rAF, men den kan
            //  köras efter denna callback – därför staplar vi explicit här. Idempotent.)
            this.stackOverlappingNotes();

            const sortedNotes = this.buildNotesBasedOnPosition();
            const noteIndex = sortedNotes.findIndex(n => n.id === note.id);
            if (noteIndex < 0) return;
            const courseCount = this.getVisibleCourseCount();

            // Markera noten (highlight + siffra) direkt, utan att componentDidUpdate auto-scrollar.
            // handleScrollActive=false skyddar valet medan panelen expanderar.
            this.suppressAutoScroll = true;
            this.setState({ currentTargetNote: courseCount + noteIndex, handleScrollActive: false });

            // Panelen expanderar med en 0.3s-transition (sidofältet breddas → dokumentet flödar
            // om → notpositionerna ändras). Vänta tills layouten satt sig och använd sedan
            // SAMMA autoscroll som navigeringsknapparna (scrollToTargetNote) – men bara om
            // kortet inte redan är synligt.
            setTimeout(() => {
                const el = document.getElementById('note-editor-' + note.id) || document.getElementById(note.id);
                const needsScroll = !!el && !this.isCardVisible(el);
                if (needsScroll) {
                    this.scrollToTargetNote(() => this.setState({ handleScrollActive: true }));
                } else {
                    this.setState({ handleScrollActive: true });
                }
            }, 450);
        });
    }

    private isCollapsedIconMode(): boolean {
        // <1200px: korten bor i mobil-modalen (noteShowOrHide renderar egna
        // NoteInlineEditor-instanser där) – sidofältet visar alltid ikoner, annars
        // skulle note-editor-{id}-elementen dubbleras i DOM:en.
        if (window.innerWidth < DESKTOP) return true;
        // Ikonen visas närhelst panelen är ihopfälld. Vid >=1460px tvingas
        // notesPanelExpaned alltid till true (se noteShowOrHide.tsx).
        return !this.props.notesPanelExpaned;
    }

    render() {
        const { showTooltip, tooltipPosition } = this.props;

        const tooltipPortalContainer = document.getElementById('document-main');
        let disableNotes = false;
        if (tooltipPortalContainer) {
            disableNotes = tooltipPortalContainer.getAttribute('data-disable-note-actions') == 'true';
        }

        let hideTools = false;
        const containerNoteHideTools = document.getElementById('notes-hide-tools');
        if (containerNoteHideTools) {
            hideTools = containerNoteHideTools.getAttribute('data-note-hide-tools') == 'true';
        }

        const sortedNotes = this.buildNotesBasedOnPosition();
        const courseCount = this.getVisibleCourseCount();
        const noteCount = sortedNotes.length;
        const totalCount = courseCount + noteCount;
        const currentIndex = this.state.currentTargetNote;
        const navNoteIndex = currentIndex - courseCount;
        const navTargetNoteId = navNoteIndex >= 0 && navNoteIndex < noteCount
            ? sortedNotes[navNoteIndex]?.id
            : null;
        const iconMode = this.isCollapsedIconMode();
        const activeFilterCount = countActiveFilters(this.props.relatedFilter);

        return (
            <div className="notesContentWrapper">
                {tooltipPortalContainer !== null &&
                    ReactDOM.createPortal(
                        <NoteTooltip
                            disableNotes={disableNotes}
                            hideTools={hideTools}
                            position={tooltipPosition}
                            onClose={this.onTooltipClose}
                            onAddMarker={this.onAddMarker}
                            onAddNote={this.onAddNote}
                            visible={showTooltip}
                        />,
                        tooltipPortalContainer
                    )
                }
                {!disableNotes &&
                    <div className="notes" id="notesOverlapContentIDHandler">
                        <div className="note note--fixed">
                            <div className="addNoteLoupe">
                                <div className={`notesSelectorWrapper${!this.props.notesPanelExpaned ? ' notesSelectorWrapper--hidden' : ''}`} id="notesSelectorWrapper">
                                    <div className="topWrapper" onClick={this.goToPrevNote}>
                                        <button
                                            className="up"
                                            aria-label="Föregående anteckning"
                                            disabled={this.state.prevNoteBtnDisabled}
                                        ></button>
                                    </div>
                                    {totalCount > 0 && (
                                        <span className="notesSelectorWrapper__counter">
                                            {Math.max(currentIndex + 1, 0)}/{totalCount}
                                        </span>
                                    )}
                                    <div className="downWrapper" onClick={this.goToNextNote}>
                                        <button
                                            className="down"
                                            aria-label="Nästa anteckning"
                                            disabled={this.state.nextNoteBtnDisabled}
                                        ></button>
                                    </div>
                                    {/* Wrapper delar border med dropdownen när panelen är öppen, så
                                        Filtrera-knappen visuellt ramas in tillsammans med panelen
                                        (Figma node 19668:45772). Nestad i notesSelectorWrapper (inte
                                        som syskon till .addNoteLoupe) eftersom moveSelector() flyttar
                                        HELA notesSelectorWrapper till header-right vid mount – panelen
                                        måste följa med för att hamna rätt under Filtrera-knappen. */}
                                    <div className={`panel-filtrera-wrapper ${this.props.filterPanelOpen ? 'panel-filtrera-wrapper--open' : ''}`}>
                                        <button
                                            className="panel-filtrera"
                                            aria-label="Filtrera anteckningar"
                                            aria-expanded={this.props.filterPanelOpen}
                                            onClick={(e) => { e.stopPropagation(); this.props.setFilterPanelOpen?.(!this.props.filterPanelOpen); }}
                                        >
                                            Filtrera
                                            {activeFilterCount > 0 && (
                                                <span className="panel-filtrera__badge">{activeFilterCount}</span>
                                            )}
                                            <span className="panel-filtrera__arrow" aria-hidden="true"></span>
                                        </button>
                                        {this.props.filterPanelOpen && (
                                            <RelatedMaterialFilter
                                                className="related-filter--dropdown"
                                                notesCount={this.props.notes.length}
                                                coursesCount={this.props.courses?.length ?? 0}
                                                filter={this.props.relatedFilter}
                                                onChange={(filter) => this.props.setRelatedFilter?.(filter)}
                                                onClose={() => this.props.setFilterPanelOpen?.(false)}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div
                                    className={`addNoteIcon js-add-note`}
                                    tabIndex={0}
                                    onKeyUp={(e) => e.key === "Enter" && this.onAddNoteClick()}
                                    onClick={this.onAddNoteClick}
                                    aria-label="Skapa en anteckning"
                                ></div>
                            </div>
                            <div className="tooltipAddNote"><span>Lägg till anteckning</span></div>
                        </div>

                        {this.props.showNotes
                            ? sortedNotes.map((note) => {
                                if (iconMode) {
                                    const iconTop = this.state.iconPositions[note.id] ?? 15;
                                    const isNoteWithText = (!!note.text && note.text.length > 0) || (!!note.title && note.title.length > 0);
                                    return (
                                        <div
                                            key={note.id}
                                            className={`note-icon note--${note.color || 'color1'}`}
                                            style={{ top: `${iconTop}px` }}
                                            title={note.title || 'Anteckning'}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => this.handleNoteIconClick(note)}
                                            onKeyUp={(e) => e.key === "Enter" && this.handleNoteIconClick(note)}
                                            aria-label={`Öppna anteckning: ${note.title || 'Anteckning'}`}
                                        >
                                            <div className="note__content">
                                                <div className={`note__icon ${isNoteWithText ? 'note__icon--note-with-text' : 'note__icon--marking'}`} />
                                            </div>
                                        </div>
                                    );
                                }
                                return (
                                    <NoteContainer key={note.id} note={note} position={true} categories={this.state.categories} navHighlighted={note.id === navTargetNoteId} hoverHighlighted={note.id === this.state.hoveredMarkingNoteId} />
                                );
                            })
                            : null}
                    </div>
                }
            </div>
        );
    }
}

// #endregion Component

// #region Redux mappers

function mapStateToProps(state: IStateTree): INoteSidebarComponentProps {
    return {
        notes: state.document.notes,
        showNotes: state.document.showNotes,
        showTooltip: state.document.showTooltip,
        tooltipPosition: state.document.tooltipPosition,
        updateNoteCollisionAndBundling: state.document.updateNoteCollisionAndBundling,
        aNoteWasUpdated: state.document.aNoteWasUpdated,
        notesPanelExpaned: state.document.notesPanelExpaned,
        currentNote: state.document.currentNote,
        editingNoteId: state.document.editingNoteId,
        courses: state.document.courses,
        relatedFilter: state.document.relatedFilter,
        filterPanelOpen: state.document.filterPanelOpen,
    };
}

function mapDispatchToProps(dispatch: any): INoteSidebarComponentDispatch {
    return {
        onHideTooltip: () => dispatch(actionCreators.hideTooltip()),
        onMarkingCreated: (note: INoteData) => dispatch(actionCreators.addMarking(note)),
        onNoteCreated: (note: INoteData) => {
            dispatch(actionCreators.addNote(note));
            // Måste sättas HÄR (samma dispatch-cykel som noten läggs till), inte i
            // noteInlineEditor.tsx:s egen componentDidMount – annars hinner filtret
            // exkludera noten på första renderingen och editorn får aldrig mount:as
            // (hönan-och-ägget: editorn kan bara sätta editingNoteId om den redan renderas).
            if (note.openEditorAtStart) {
                dispatch(setEditingNoteId(note.id));
            }
        },
        onNoteOpenToggleVisibility: (visibility: boolean) => {
            dispatch(actionCreators.setNotesPanelExpaned(visibility));
            dispatch(actionCreators.setRollDownExpanded(false));
        },
        rollDownExpanded: (visibility: boolean) => {
            dispatch(actionCreators.setRollDownExpanded(visibility));
        },
        setNotesPanelExpaned: (visibility: boolean) => {
            dispatch(actionCreators.setNotesPanelExpaned(visibility));
        },
        setCurrentNote: (note: INoteData) => {
            dispatch(actionCreators.setCurrentNote(note));
        },
        aNoteWasUpdatedFunc: () => {
            dispatch(actionCreators.aNoteWasUpdated());
        },
        setHighlightedCourseIndex: (index: number | null) => {
            dispatch(setHighlightedCourseIndex(index));
        },
        setRelatedFilter: (filter) => {
            dispatch(setRelatedFilter(filter));
        },
        setFilterPanelOpen: (open: boolean) => {
            dispatch(setFilterPanelOpen(open));
        },
    };
}

// #endregion Redux mappers

// #region Smart container

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(NoteSidebarComponent);

// #endregion Smart container
